import { useItemImageFailure } from "@/hooks/use-item-image-failure";
import { useResetOnKeyChange } from "@/hooks/use-reset-on-key-change";
import { AppDialog } from "@/components/ui/app-dialog";
import { ItemEmptyImageBox } from "@/components/ui/item-image-box";
import { ItemPicture } from "@/components/ui/item-picture";
import { ItemThumbnailStrip } from "@/components/ui/item-thumbnail-strip";
import {
  ITEM_PLACEHOLDER_ALT,
  itemImageSources,
  type CatalogItemImageInput,
} from "@/lib/catalog-item-image";

// One of an item's images, opened large — spec #269, user story 8.
//
// **Net new, and it has no donor.** The `Lightbox/` directory in
// `ItemDetailsIsland` is a vestigial name: git history shows it has only ever
// held `Images` plus its tests, and a case-insensitive grep for "lightbox"
// across the donor's `src/` and `packages/` matches nothing but that path. In
// modsy, "inspect at large size" *is* the `maxHeight: 350px` main viewer — the
// gallery's largest picture — and clicking it does nothing
// (`ImagePreview.tsx:44`). Spec #269 (stories 7-8) rules this dialog as the
// Atlas rendering of that capability, so what this file owes is the capability
// set — see all images, view any one large, hero first — and not a shape to copy.
//
// **In the catalog folder, not `components/ui/`**, precisely because of that: the
// shared-donor rule sends a component to `ui/` when it is shared *in the source*
// (`docs/agents/migration-guidelines.md`), and this one is in no source at all.
// It is named for item images, shaped by one screen's spec, and reached only from
// `item-gallery.tsx` beside it. The pieces it is built from — the dialog chrome,
// the `<picture>`, the thumbnail strip — are the shared parts, and they do live
// in `ui/`.
//
// It reuses the app's own dialog chrome (`AppDialog`, itself the port of the
// donor's shared `packages/react-ui/src/dialog/*`) rather than inventing a second
// modal idiom for one screen.

interface ItemImageLightboxProps {
  images: readonly CatalogItemImageInput[];
  /** The item's name, for the dialog's accessible name and each image's alt. */
  itemName: string | null | undefined;
  /**
   * Which image to open on, or `null` for closed.
   *
   * The index rather than a boolean plus a separate index: "open" and "open on
   * *which*" are one fact, and two props would let a caller state a lightbox
   * that is open on nothing.
   */
  openAt: number | null;
  onOpenChange: (open: boolean) => void;
  /** Whether the item is approved — the placeholder's pending variant. */
  isApproved?: boolean;
}

/**
 * How wide this dialog's picture is actually drawn, as a `sizes` list — named
 * here for the same reason `add-variant-preview-card.tsx` names its own: the
 * number is derived from the layout in this file, which is the only place it
 * exists.
 *
 * **The slot's upper bound, which is what `sizes` must state**: the dialog's
 * 1200px paper less the 80px of body gutters, and the viewport less those
 * gutters below that.
 *
 * Deliberately *not* the painted width. `object-contain` under `max-h-[70vh]`
 * makes this slot **height**-bound for anything squarer than ~1.79:1, which is
 * most of the corpus: at 1440x900 the height cap is 630px, so a 1:1 source
 * paints 630x630 and a 4:3 one 840x630, against the 1120 stated here. `sizes`
 * has no way to express that — it is a width-only hint, evaluated before the
 * image's aspect is known.
 *
 * Overstating is the safe direction: the browser fetches a rung at least as
 * wide as the slot and downsamples, where understating blurs. It costs one rung
 * on the height-bound case — a 2048px-wide 1:1 source picks `w2048` for that
 * 630px box where `w1024` would have covered it, a few tens of KB. And w2048
 * is a ladder step for this slot's sake in the first place: 1120px at a 2x
 * pixel ratio asks for ~2240px, which wide sources and large viewports do
 * reach.
 */
const LIGHTBOX_SIZES = "(min-width: 1200px) 1120px, calc(100vw - 8rem)";

export function ItemImageLightbox({
  images,
  itemName,
  openAt,
  onOpenChange,
  isApproved = true,
}: ItemImageLightboxProps) {
  // Where the dialog is *now*: whatever opened it, and then wherever the strip
  // moves it.
  //
  // Keyed on the opening index **and the set**. The index alone re-opens on the
  // right picture rather than where the last visit left off, but it does not
  // change when the images underneath do — so a variant switch left the dialog
  // showing "image 3" of a set whose third picture was now something else
  // entirely. The page's main slot is keyed on the set for the same reason.
  const setKey = images.map((image) => image.id).join(",");
  const [browsing, setBrowsing] = useResetOnKeyChange(`${openAt}\n${setKey}`, openAt ?? 0);

  const open = openAt !== null;
  // A closed dialog still renders (Radix owns the mount), and an index past a
  // shrunken set — an image whose fetch failed, a removed one — must not read
  // undefined.
  const index = browsing < images.length ? browsing : 0;
  const current = images[index];

  // This dialog draws the ladder, like every other slot — but at its own
  // `sizes`, so the rung it picks is a different fetch from the page's and gets
  // its own verdict (`useItemImageFailure`).
  //
  // **Above the guard below, because it is a hook**: an item whose images have
  // gone away must not change how many hooks this component calls. The empty
  // bundle it stands on in that case is never rendered.
  const sources = itemImageSources(current);
  const [failed, noteFailed] = useItemImageFailure(sources);

  if (current === undefined) return null;
  const name = itemName?.trim();
  const showStrip = images.length > 1;

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      // The picture wants the room; `lg` is MUI's 1200px, the wider of the two
      // sizes the ported dialogs ask for.
      size="lg"
      // The position is in the title because a dialog needs a name and this is
      // the useful one: it is also what tells the reader which of the six is on
      // screen, which is why the strip below adds no marker of its own.
      title={name ? `${name} — image ${index + 1} of ${images.length}` : "Item image"}
    >
      {failed ? (
        // The same fallback the page draws, at the size this dialog has room
        // for: a picture that would not load on the page will not load here.
        <ItemEmptyImageBox
          isPendingApproval={!isApproved}
          alt={ITEM_PLACEHOLDER_ALT}
          heightPx={350}
          bordered="light"
        />
      ) : (
        <ItemPicture
          data-testid="lightbox-image"
          // **The ladder, like every other slot** (#499). This used to be the
          // one original-backed picture on the page, because the ladder topped
          // out at w1024 — so its top rung was a ceiling on a 1120px slot
          // rather than the picture. The steps reach w2048 now, and a source
          // wide enough to have that rung has it (#508), so the slot's own
          // `sizes` below picks the right one and a 2x display gets ~2240px
          // worth of detail instead of one unbounded original. A narrower
          // source tops out at its own width — all the detail there ever was.
          sources={sources}
          // The bare item name, as on the page — with the image's position
          // added, because in here the position is the only thing telling one
          // picture from another.
          alt={name ? `${name} — image ${index + 1}` : `Image ${index + 1}`}
          sizes={LIGHTBOX_SIZES}
          // Contained, so the whole picture is visible whatever its aspect, and
          // bounded by the viewport rather than by a fixed height: the point of
          // this dialog is detail the 350px viewer could not show.
          className="mx-auto block max-h-[70vh] w-auto max-w-full object-contain"
          loading="eager"
          onError={noteFailed}
        />
      )}

      {showStrip && (
        // The same strip as the page's — the same component, so "see all the
        // images" survives the step up in size and cannot drift out of step
        // with the page's copy. Stepping through them here costs no
        // close-and-reopen.
        <ItemThumbnailStrip
          data-testid="lightbox-thumbnails"
          images={images}
          onSelect={setBrowsing}
          isApproved={isApproved}
          className="mt-6 justify-center"
        />
      )}
    </AppDialog>
  );
}
